import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { auth, signInWithGoogle, signOut, getEnvironmentConfig } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";
import { defaultDashboardSummary, fetchDashboardSummary } from "./dashboardData";
import { prefetchRecentUserVideos } from "./videoLibraryData";
import "./App.css";

const MAX_FILE_SIZE = 500 * 1024 * 1024;
const { bucket: BUCKET } = getEnvironmentConfig();

function formatFileSize(bytes) {
  if (!bytes) return "0 MB";
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function uploadFile(url, file, onProgress) {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open("PUT", url);
    request.setRequestHeader("Content-Type", file.type || "video/mp4");
    request.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(Math.round((event.loaded / event.total) * 100));
    };
    request.onload = () => {
      if (request.status >= 200 && request.status < 300) resolve();
      else reject(new Error("The video could not be uploaded."));
    };
    request.onerror = () => reject(new Error("The upload connection was interrupted."));
    request.onabort = () => reject(new Error("The upload was cancelled."));
    request.send(file);
  });
}

class DashboardErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    console.error("Dashboard render failed:", error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <main className="app-shell auth-screen">
          <section className="auth-card">
            <p className="eyebrow">EVOLVE SPORTS</p>
            <h1>Your dashboard needs a quick reset.</h1>
            <p className="lead">Your payment and serve data are safe. Reload to continue.</p>
            <button className="button button-primary button-wide" onClick={() => window.location.reload()}>
              Reload dashboard
            </button>
          </section>
        </main>
      );
    }

    return this.props.children;
  }
}

function AppContent() {
  const [user, setUser] = useState(null);
  const [file, setFile] = useState(null);
  const [uploadState, setUploadState] = useState("idle");
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState("");
  const [captureConfirmed, setCaptureConfirmed] = useState(false);
  const [dashboardData, setDashboardData] = useState(defaultDashboardSummary);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [checkoutResult] = useState(
    () => new URLSearchParams(window.location.search).get("checkout")
  );
  const fileInputRef = useRef(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, setUser);
    return unsubscribe;
  }, []);

  useEffect(() => {
    let active = true;

    async function loadDashboard() {
      if (!user) {
        setDashboardLoading(false);
        return;
      }

      setDashboardLoading(true);
      try {
        const idToken = await user.getIdToken();
        const summary = await fetchDashboardSummary(idToken);
        if (active) {
          setDashboardData(summary);
        }
      } catch (error) {
        console.warn("Dashboard load failed:", error);
      } finally {
        if (active) {
          setDashboardLoading(false);
        }
      }
    }

    loadDashboard();
    return () => {
      active = false;
    };
  }, [user]);

  useEffect(() => {
    prefetchRecentUserVideos(user);
  }, [user]);

  useEffect(() => {
    if (!checkoutResult) return;
    window.history.replaceState(null, "", window.location.pathname);
  }, [checkoutResult]);

  useEffect(() => {
    if (!user || checkoutResult !== "success") return undefined;

    let active = true;
    let timerId;
    let attempt = 0;

    async function refreshEntitlement() {
      attempt += 1;
      try {
        if (attempt === 1) {
          const response = await fetch("/api/billing/sync", {
            method: "POST",
            headers: {
              Authorization: `Bearer ${await user.getIdToken(true)}`,
            },
          });
          if (!response.ok && response.status !== 404) {
            const payload = await response.json();
            throw new Error(payload.error || "Could not confirm Player access.");
          }
        }
        const summary = await fetchDashboardSummary(await user.getIdToken(true));
        if (!active) return;
        setDashboardData(summary);
        if (!summary.usage.hasSubscriptionAccess && attempt < 7) {
          timerId = window.setTimeout(refreshEntitlement, 3000);
        }
      } catch (error) {
        console.warn("Checkout entitlement refresh failed:", error);
      }
    }

    refreshEntitlement();
    return () => {
      active = false;
      if (timerId) window.clearTimeout(timerId);
    };
  }, [user, checkoutResult]);

  function selectFile(nextFile) {
    if (!nextFile) return;
    if (!nextFile.type.startsWith("video/")) {
      setMessage("Please choose a video file.");
      return;
    }
    if (nextFile.size > MAX_FILE_SIZE) {
      setMessage("Please choose a video smaller than 500 MB.");
      return;
    }
    setFile(nextFile);
    setMessage("");
    setUploadState("idle");
    setProgress(0);
  }

  function handleDrop(event) {
    event.preventDefault();
    selectFile(event.dataTransfer.files[0]);
  }

  async function handleUpload() {
    if (!file || !user || !captureConfirmed) return;
    setUploadState("uploading");
    setProgress(0);
    setMessage("Preparing your analysis...");

    try {
      const contentType = file.type || "video/mp4";
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      const videoId = `match_${timestamp}.mp4`;
      const objectPath = `${user.uid}/input/${videoId}`;
      const params = new URLSearchParams({
        bucket: BUCKET,
        objectPath,
        contentType,
      });
      const idToken = await user.getIdToken();
      const response = await fetch(`/api/createSignedUploadUrl?${params}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${idToken}` },
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Could not prepare the upload.");
      const { url } = result;
      if (!url) throw new Error("The upload service did not return a URL.");

      setMessage("Uploading video...");
      await uploadFile(url, file, setProgress);
      setUploadState("complete");
      localStorage.setItem("pendingVideo", videoId);
      setMessage("Upload complete. Your coaching report is processing now.");
      window.location.assign(
        `/library?pending_video=${encodeURIComponent(videoId)}&pending_name=${encodeURIComponent(file.name)}`
      );
    } catch (error) {
      console.error("Upload failed:", error);
      setUploadState("error");
      setMessage(error.message || "Upload failed. Please try again.");
    }
  }

  async function startBilling(path) {
    setMessage("");
    try {
      const idToken = await user.getIdToken();
      const response = await fetch(path, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
      });
      const payload = await response.json();
      if (!response.ok || !payload.url) {
        throw new Error(payload.error || "Could not open billing.");
      }
      window.location.assign(payload.url);
    } catch (error) {
      console.error("Billing request failed:", error);
      setMessage(error.message || "Could not open billing. Please try again.");
    }
  }

  if (!user) {
    return (
      <main className="app-shell auth-screen">
        <section className="auth-card">
          <div className="brand-mark">🎾</div>
          <p className="eyebrow">TENNIS ANALYTICS</p>
          <h1>Turn every serve into a smarter next rep.</h1>
          <p className="lead">
            Upload a serve or match video and get frame-by-frame coaching insights built for
            your game.
          </p>
          <button className="button button-primary button-wide" onClick={signInWithGoogle}>
            Continue with Google
          </button>
          <p className="fine-print">Your videos stay private to your account.</p>
        </section>
      </main>
    );
  }

  const metrics = dashboardData.metrics.map((item) => ({
    ...item,
    value: `${item.value}${item.unit || "%"}`,
  }));

  const focusAreas = dashboardData.focusAreas;
  const recentSessions = dashboardData.recentSessions;
  const isFirstServe = !dashboardLoading && dashboardData.totalServes === 0;
  const usage = dashboardData.usage;
  const canStartAnalysis = usage.hasUnlimitedAccess || usage.hasSubscriptionAccess || usage.freeRemaining > 0;
  const usageLabel = usage.hasUnlimitedAccess
    ? "Unlimited analyses included"
    : usage.hasSubscriptionAccess
      ? "Player plan active · Unlimited analyses included"
    : `${usage.freeRemaining} of ${usage.freeLimit} free analyses remaining`;
  const completedFreeAnalyses = Math.min(
    usage.freeLimit,
    Math.max(0, usage.completedAnalyses)
  );
  const showMembershipPanel = !dashboardLoading && !usage.hasUnlimitedAccess;
  const membershipIsActive = usage.hasSubscriptionAccess;
  const membershipTitle = membershipIsActive
    ? "Your coaching loop is open."
    : usage.freeRemaining > 0
      ? "Build your coaching rhythm."
      : "Your next level is ready.";
  const membershipCopy = membershipIsActive
    ? "Player keeps every serve, replay, and next rep connected."
    : usage.freeRemaining > 0
      ? "Your first five serve reads are on us. Continue whenever you are ready."
      : "You have used your five free serve reads. Continue with unlimited analysis.";

  return (
    <main className="app-shell">
      <header className="topbar">
        <Link className="wordmark" to="/">
          <span className="wordmark-icon">🎾</span>
          <span>Evolve <span className="wordmark-accent">Sports</span></span>
        </Link>
        <div className="account">
          {user.photoURL ? <img src={user.photoURL} alt="" className="avatar" /> : null}
          <span className="account-name">{user.displayName || user.email}</span>
          <button className="button button-quiet" onClick={() => signOut(auth)}>Sign out</button>
        </div>
      </header>

      <div className="dashboard">
        <section className="hero">
          <div>
            <p className="eyebrow">YOUR COACHING HUB</p>
            <h1>{isFirstServe ? "Your first great serve starts here." : "Ready to build your next rep?"}</h1>
            <p className="hero-copy">{isFirstServe ? "Upload a serve and unlock a clear plan for your next rep." : "Upload a fresh serve and turn your motion into your next move."}</p>
          </div>
          <div className="hero-stat">
            <strong>{dashboardLoading ? "—" : dashboardData.totalServes}</strong>
            <span>Serves<br />analysed</span>
          </div>
        </section>

        {checkoutResult === "success" ? (
          <section className={`status-banner ${membershipIsActive ? "status-banner-ready" : "status-banner-processing"}`}>
            <span>{membershipIsActive ? "✓" : "⏳"}</span>
            <div>
              <strong>{membershipIsActive ? "Player is unlocked. Let’s build your next rep." : "Payment received. Activating your Player plan…"}</strong>
              <small>{membershipIsActive ? "Unlimited analysis is now available for this account." : "This normally takes a few seconds. You can keep using the dashboard while we confirm it."}</small>
            </div>
          </section>
        ) : null}

        {isFirstServe ? (
          <section className="onboarding-card">
            <div className="onboarding-heading">
              <div>
                <p className="eyebrow">YOUR FIRST ANALYSIS</p>
                <h2>From video to a better next rep.</h2>
              </div>
              <span className="step-badge">About 3 minutes</span>
            </div>
            <div className="onboarding-steps">
              <div><span>1</span><strong>Upload a serve</strong><small>Choose a clear video from your phone or computer.</small></div>
              <div><span>2</span><strong>We read the motion</strong><small>We measure key phases of your serve automatically.</small></div>
              <div><span>3</span><strong>Train with a plan</strong><small>Review your priorities, drill, and progress.</small></div>
            </div>
            <button className="button button-primary onboarding-action" onClick={() => fileInputRef.current?.click()}>
              Choose your first video
            </button>
          </section>
        ) : null}

        <section className="workspace-grid">
          <div className="upload-card">
            <div className="section-heading">
              <div>
                <p className="eyebrow">NEW SERVE REP</p>
                <h2>Build your serve</h2>
                {!dashboardLoading ? <p className="analysis-usage">{usageLabel}</p> : null}
              </div>
              <span className="step-badge">1 of 2</span>
            </div>
            <section className="capture-coach">
              <div className="capture-coach-heading">
                <div>
                  <p className="eyebrow">CAPTURE COACH</p>
                  <h3>Set up one serve for a clearer read.</h3>
                </div>
                <span>Camera-view analysis</span>
              </div>
              <div className="capture-guide">
                <div className="capture-diagram" aria-hidden="true">
                  <i className="capture-baseline" />
                  <span className="capture-camera">●<small>Phone</small></span>
                  <span className="capture-player">♙<small>Server</small></span>
                  <b>Side-on view</b>
                </div>
                <ul>
                  <li><strong>Side-on and level</strong><span>Place the phone around hip height, perpendicular to the baseline.</span></li>
                  <li><strong>Keep the whole motion visible</strong><span>Leave room for both feet, the tossing arm, racket, and follow-through.</span></li>
                  <li><strong>Make it stable and bright</strong><span>Use a tripod or support; avoid heavy backlight, blur, and handheld panning.</span></li>
                </ul>
              </div>
              <label className="capture-confirmation">
                <input type="checkbox" checked={captureConfirmed} onChange={(event) => setCaptureConfirmed(event.target.checked)} />
                <span>I&apos;m uploading one serve with the full motion clearly visible.</span>
              </label>
            </section>
            <div
              className={`dropzone ${file ? "dropzone-selected" : ""}`}
              onDragOver={(event) => event.preventDefault()}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              role="button"
              tabIndex="0"
              onKeyDown={(event) => event.key === "Enter" && fileInputRef.current?.click()}
            >
              <input ref={fileInputRef} type="file" accept="video/*" hidden onChange={(event) => selectFile(event.target.files[0])} />
              <span className="upload-icon">{file ? "✓" : "↑"}</span>
              <strong>{file ? file.name : "Drop your video here"}</strong>
              <span>{file ? formatFileSize(file.size) : "or click to browse · MP4, MOV, or WebM"}</span>
            </div>
            {uploadState === "uploading" ? (
              <div className="progress-wrap" aria-label={`Upload progress: ${progress}%`}>
                <div className="progress-label"><span>Uploading</span><strong>{progress}%</strong></div>
                <div className="progress-track"><div className="progress-bar" style={{ width: `${progress}%` }} /></div>
              </div>
            ) : null}
            {message ? <p className={`status-message status-${uploadState}`} role="status">{message}</p> : null}
            <button className="button button-primary button-upload" disabled={!file || uploadState === "uploading" || !canStartAnalysis || !captureConfirmed} onClick={handleUpload}>
              {uploadState === "uploading" ? "Uploading..." : uploadState === "complete" ? "Upload another video" : "Start analysis"}
            </button>
            <p className="fine-print">
              {!canStartAnalysis ? "Your five free analyses are complete. Continue with Player to keep building your serve." : !captureConfirmed ? "Confirm the setup above to unlock your analysis." : "Maximum 500 MB · Your serve is usually ready in a few minutes"}
            </p>
          </div>

          <aside className="training-rail">
            {showMembershipPanel ? (
              <section className={`membership-panel ${membershipIsActive ? "membership-active" : ""}`}>
                <div className="membership-path" aria-hidden="true">
                  <span>◌</span><i /><span>◌</span><i /><span>◌</span>
                </div>
                <p className="eyebrow">{membershipIsActive ? "PLAYER PLAN" : "YOUR COACHING PATH"}</p>
                <h2>{membershipTitle}</h2>
                <p className="membership-copy">{membershipCopy}</p>
                {!membershipIsActive ? (
                  <div className="analysis-markers" aria-label={`${completedFreeAnalyses} of ${usage.freeLimit} free analyses completed`}>
                    {Array.from({ length: usage.freeLimit }, (_, index) => (
                      <span className={index < completedFreeAnalyses ? "analysis-marker complete" : "analysis-marker"} key={index}>
                        {index < completedFreeAnalyses ? "✓" : "•"}
                      </span>
                    ))}
                  </div>
                ) : null}
                <div className="membership-includes">
                  <span>Unlimited serve reads</span>
                  <span>Motion replay</span>
                  <span>Progress over time</span>
                </div>
                <button
                  className="button button-primary membership-action"
                  onClick={() => startBilling(membershipIsActive ? "/api/billing/portal" : "/api/billing/checkout")}
                >
                  {membershipIsActive ? "Manage Player plan" : "Continue with Player · $19/month"}
                </button>
              </section>
            ) : null}
            <section className="next-card">
              <p className="eyebrow">KEEP BUILDING</p>
              <h2>Your training loop</h2>
              <nav className="quick-links" aria-label="Training shortcuts">
                <Link to="/library"><span>▣</span><div><strong>Serve library</strong><small>Review every rep you have built</small></div><b>→</b></Link>
                <Link to="/analytics"><span>↗</span><div><strong>Progress checkpoint</strong><small>See what your reps are building</small></div><b>→</b></Link>
                <Link to="/cx-chat"><span>◌</span><div><strong>Ask your coach</strong><small>Get answers about your serve</small></div><b>→</b></Link>
              </nav>
            </section>
          </aside>
        </section>

        <section className="promise-row">
          <div><span>✦</span><div><strong>See the proof</strong><small>Frame-level motion analysis</small></div></div>
          <div><span>↗</span><div><strong>Train the gap</strong><small>One clear next move</small></div></div>
          <div><span>⌁</span><div><strong>Build your serve</strong><small>Every rep, all in one place</small></div></div>
        </section>

        <section className="insights-grid">
          <article className="insights-panel">
            <div className="section-heading compact">
              <div>
                <p className="eyebrow">PERFORMANCE</p>
                <h2>Coaching snapshot</h2>
              </div>
              <span className="step-badge subtle">7 day view</span>
            </div>
            <div className="metric-grid">
              {dashboardLoading ? (
                <div className="empty-state">Loading analytics…</div>
              ) : (
                metrics.map((item) => (
                  <div key={item.label} className="metric-card">
                    <span>{item.label}</span>
                    <strong>{item.value}</strong>
                    <small>{item.trend}</small>
                  </div>
                ))
              )}
            </div>
          </article>

          <article className="focus-panel">
            <div className="section-heading compact">
              <div>
                <p className="eyebrow">FOCUS</p>
                <h2>Today’s plan</h2>
              </div>
            </div>
            <ul className="focus-list">
              {focusAreas.map((item) => (
                <li key={item.name} className={item.tone === "watch" ? "watch" : "good"}>
                  <div>
                    <span>{item.name}</span>
                    <strong>{item.score}%</strong>
                  </div>
                  <div className="mini-bar">
                    <i style={{ width: `${item.score}%` }} />
                  </div>
                </li>
              ))}
            </ul>
          </article>
        </section>

        <section className="sessions-panel">
          <div className="section-heading compact sessions-header">
            <div>
              <p className="eyebrow">RECENT REPS</p>
              <h2>What you are building</h2>
            </div>
            <button className="button button-quiet secondary-link">View all</button>
          </div>

          <div className="session-grid">
            {recentSessions.map((session) => (
              <article key={session.title} className="session-card">
                <div className="session-topline">
                  <span className="session-tag">{session.tag}</span>
                  <strong>{session.score}</strong>
                </div>
                <h3>{session.title}</h3>
                <p>{session.date}</p>
                <div className="session-actions">
                  <button className="button button-primary small-button">Open</button>
                  <button className="button button-quiet small-button ghost">Share</button>
                </div>
              </article>
            ))}
          </div>
        </section>
      </div>
    </main>
  );
}

export default function App() {
  return (
    <DashboardErrorBoundary>
      <AppContent />
    </DashboardErrorBoundary>
  );
}
